import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import HotelSettingsMyotbMyotbBaseController from 'lumen/controllers/hotel/settings/myotb/myotb-base';
import {
  ELIGIBILITY_QUESTION_TYPES,
  type EligibilityQuestionType,
  STAGES,
} from 'lumen/utils/constants/otb-selfservice-constants';

type Answer = string;

type EligibilityQuestion = {
  answers: Answer[];
  name: string;
  options: string[];
  questionType: EligibilityQuestionType;
};

type PmsdatasourceEligibilityQuestion = Omit<
  EligibilityQuestion,
  'questionType'
> & {
  question_type: EligibilityQuestionType;
};

export default class HotelSettingsMyotbOtherSystemEligibilityController extends HotelSettingsMyotbMyotbBaseController {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare router: Services['router'];

  @tracked eligibilityQuestions: EligibilityQuestion[] | null = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get eligibilityQuestionsAnswers() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.otbSelfService.otherSystemEligibilityQuestionsAnswers;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsDatasource() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.otbSelfService.otherSystem;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async onClickContinue() {
    // Set selected properties on the local self-service object
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.otbSelfService.otherSystemEligibilityQuestionsAnswers =
      // @ts-expect-error: Incorrect type
      this.eligibilityQuestions.map((eligibilityQuestion) => {
        const { answers, name } = eligibilityQuestion;

        return { answers, name };
      });

    // Save the self-service object
    try {
      this.isLoading = true;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await this.otbSelfService.save();

      // Redirect based on the updated otb self-service object returned by the API in the save call
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (this.otbSelfService.stage === STAGES.PMS_UNSUPPORTED_PAGE) {
        // User's other system setup is unsupported.
        this.router.transitionTo('hotel.settings.myotb.pms-unsupported');
      } else {
        // User's other system setup is supported.
        this.router.transitionTo('hotel.settings.myotb.pms-setup');
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(error);
    } finally {
      this.isLoading = false;
    }
  }

  @action onClickPrevious(): void {
    this.router.transitionTo('hotel.settings.myotb.other-system-selection');
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setAnswers(eligibilityQuestionName: unknown, answers: Answer[]) {
    // Create a new eligibility questions array to trigger state updates.
    const newEligibilityQuestions = [...this.eligibilityQuestions!];

    // Create a new eligibility question object to trigger state updates.
    const index = newEligibilityQuestions.findIndex((question) => {
      return question.name === eligibilityQuestionName;
    });

    const newEligibilityQuestion = {
      ...newEligibilityQuestions[index]!,
      answers,
    };

    newEligibilityQuestions[index] = newEligibilityQuestion;

    this.eligibilityQuestions = newEligibilityQuestions;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setDefaults() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { eligibilityQuestions } = this.pmsDatasource;

    if (!eligibilityQuestions) {
      return;
    }

    this.eligibilityQuestions = (
      eligibilityQuestions as PmsdatasourceEligibilityQuestion[]
    ).map((eligibilityQuestion) => {
      const {
        name,
        options,
        question_type: questionType,
      } = eligibilityQuestion;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const answers: Answer[] =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.eligibilityQuestionsAnswers?.find((answer) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          return answer.name === name;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        })?.answers ?? [];

      // Question type single requires that at least one of the options is selected, so push the first option to the answers.
      if (
        questionType === ELIGIBILITY_QUESTION_TYPES.SINGLE &&
        answers.length === 0
      ) {
        answers.push(options[0]!);
      }

      return {
        answers,
        name,
        options,
        questionType,
      };
    });
  }
}
